/** ISO 4217 codes supported for rate cards and line items. */
export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  CAD = 'CAD'
}

export type CurrencyCode = `${Currency}`;

export const DEFAULT_CURRENCY: CurrencyCode = Currency.GBP;

export const CURRENCY_OPTIONS: { label: string; value: CurrencyCode }[] = [
  { label: 'GBP (£)', value: Currency.GBP },
  { label: 'USD ($)', value: Currency.USD },
  { label: 'EUR (€)', value: Currency.EUR },
  { label: 'CAD (C$)', value: Currency.CAD }
];

const SYMBOL_BY_CURRENCY: Record<CurrencyCode, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: 'C$'
};

export function getCurrencySymbol(currency?: string | null): string {
  const code = (currency?.trim().toUpperCase() ?? DEFAULT_CURRENCY) as CurrencyCode;
  return SYMBOL_BY_CURRENCY[code] ?? code;
}

/** Display monetary values with the row/plan currency (falls back to GBP). */
export function formatMoney(value: number | undefined, currency?: string | null): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  const code = currency?.trim().toUpperCase();
  try {
    if (code && /^[A-Z]{3}$/.test(code)) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  } catch {
    /* fall through */
  }
  /* No usable code: the symbol is still resolved, but the number is grouped
     and fixed to two places rather than printed raw -- a float straight from
     the API renders as £11899.972687170397, which reads as broken. */
  return `${getCurrencySymbol(currency)}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

/**
 * A large monetary total, shortened to its magnitude.
 *
 * Summary figures span the whole booking database and run to eight digits,
 * where the pennies are noise and the size of the number is what is being
 * read — and a raw float total shows them as false precision
 * (£51,928,510.264, three decimals on money). Millions and thousands are
 * abbreviated; anything smaller keeps the exact amount, where it still means
 * something.
 */
export function formatCompactMoney(value: number | undefined, currency?: string | null): string {
  if (value == null || Number.isNaN(value)) return 'N/A';

  const abs = Math.abs(value);
  const [divisor, suffix] =
    abs >= 1_000_000 ? [1_000_000, 'M'] : abs >= 1_000 ? [1_000, 'K'] : [0, ''];

  // Below a thousand there is nothing to abbreviate, so the exact figure stands.
  if (!divisor) return formatMoney(value, currency);

  /* Scaled on the absolute value and re-signed once at the end. Formatting the
     signed number instead leaves the sign formatMoney already emitted in place
     and adds a second one from the digits being substituted (-£-2.40M). */
  const scaled = abs / divisor;
  /* Half up, as every figure is: toFixed(2) alone sends 11.475 -- held as
     11.47499... -- down to 11.47, so €11,475 read as €11.47K. The nudge is far
     below the last digit shown. */
  const shown = (Math.round(scaled * 100 + 1e-9) / 100).toFixed(2);
  const base = formatMoney(scaled, currency).replace(/[\d.,]+/, shown);
  return `${value < 0 ? '-' : ''}${base}${suffix}`;
}
